import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar, Clock, MapPin, Building2, XCircle, BookOpen, Mic } from 'lucide-react';
import { partialExamAPI, mockExamAPI } from '@/api/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { formatDate, formatTime12Hour, formatTimeRange } from '@/utils/formatters';

const MyBookingsTab = () => {
  const [partialBookings, setPartialBookings] = useState([]);
  const [mockBookings, setMockBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelType, setCancelType] = useState(null); // 'partial' or 'mock'
  const [canceling, setCanceling] = useState(false);
  const [cancelInfo, setCancelInfo] = useState(null);
  const { refreshCredits } = useAuth();

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const [partialRes, mockRes] = await Promise.all([
        partialExamAPI.getMyBookings(),
        mockExamAPI.getMyBookings()
      ]);
      setPartialBookings(partialRes.data || []);
      setMockBookings(mockRes.data || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const canCancel = (examDate) => {
    const today = new Date();
    const exam = new Date(examDate);
    const diffTime = exam - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 3;
  };

  const handleCancelClick = async (booking, type) => {
    try {
      // Fetch cancellation info from backend
      const response = await partialExamAPI.getCancellationInfo();
      setCancelInfo(response.data);
      setSelectedBooking(booking);
      setCancelType(type);
      setCancelDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load cancellation info');
    }
  };

  const confirmCancel = async () => {
    if (!selectedBooking) return;

    try {
      setCanceling(true);
      
      if (cancelType === 'partial') {
        await partialExamAPI.cancel({
          booking_id: selectedBooking.id,
          reason: 'Student initiated cancellation'
        });
        toast.success('Partial exam cancelled successfully');
      } else if (cancelType === 'mock') {
        await mockExamAPI.cancel({
          mock_booking_id: selectedBooking.id,
          part: 'LRW',
          reason: 'Student initiated cancellation'
        });
        toast.success('Mock test cancelled successfully');
      }

      // Refresh bookings and credits
      await fetchBookings();
      await refreshCredits();
      setCancelDialogOpen(false);
      setSelectedBooking(null);
      
      // Notify other tabs to refresh
      window.dispatchEvent(new Event('bookingCancelled'));
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to cancel booking';
      toast.error(errorMsg);
    } finally {
      setCanceling(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      BOOKED: 'default',
      CANCELLED: 'destructive',
      ATTENDED: 'outline',
      NO_SHOW: 'secondary'
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  const upcomingPartial = partialBookings.filter(b => b.status === 'BOOKED');
  const upcomingMock = mockBookings.filter(b => b.lrw_status === 'BOOKED');

  return (
    <div className="space-y-6">
      {/* Partial Exam Bookings */}
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
          Partial Exam Bookings
        </h2>
        
        {upcomingPartial.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              No partial exam bookings found
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {upcomingPartial.map((booking) => {
              const isCancelable = canCancel(booking.exam_date);
              
              return (
                <Card key={booking.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                          <Badge variant="outline" className="text-sm sm:text-base">
                            {booking.module}
                          </Badge>
                          {getStatusBadge(booking.status)}
                        </CardTitle>
                      </div>
                      {booking.status === 'BOOKED' && (
                        <Button
                          variant={isCancelable ? "destructive" : "outline"}
                          size="sm"
                          disabled={!isCancelable}
                          onClick={() => handleCancelClick(booking, 'partial')}
                          title={!isCancelable ? "Cannot cancel within 3 days of exam" : "Cancel booking"}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        <span>{formatDate(booking.exam_date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span>{formatTimeRange(booking.start_time, booking.end_time)}</span>
                      </div>
                      {booking.room && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <MapPin className="w-4 h-4 text-blue-600" />
                          <span>{booking.room}</span>
                        </div>
                      )}
                      {booking.campus && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Building2 className="w-4 h-4 text-blue-600" />
                          <span>{booking.campus}</span>
                        </div>
                      )}
                    </div>
                    {!isCancelable && booking.status === 'BOOKED' && (
                      <p className="mt-3 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                        ⚠️ Cannot cancel within 3 days of exam date
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Mock Test Bookings */}
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Mic className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
          Mock Test Bookings
        </h2>
        
        {upcomingMock.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              No mock test bookings found
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {upcomingMock.map((booking) => {
              const isCancelable = canCancel(booking.lrw_date);
              
              return (
                <Card key={booking.id} className="hover:shadow-lg transition-shadow border-purple-200">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                          <Badge variant="outline" className="text-sm sm:text-base bg-purple-50">
                            Mock Test - {booking.mock_uid}
                          </Badge>
                          {getStatusBadge(booking.lrw_status)}
                        </CardTitle>
                      </div>
                      {booking.lrw_status === 'BOOKED' && (
                        <Button
                          variant={isCancelable ? "destructive" : "outline"}
                          size="sm"
                          disabled={!isCancelable}
                          onClick={() => handleCancelClick(booking, 'mock')}
                          title={!isCancelable ? "Cannot cancel within 3 days of exam" : "Cancel mock test"}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel Mock
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="bg-blue-50 p-3 rounded">
                        <h4 className="font-semibold text-sm mb-2">LRW Session</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-700">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span>{formatDate(booking.lrw_date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-700">
                            <Clock className="w-4 h-4 text-blue-600" />
                            <span>{formatTimeRange(booking.lrw_start, booking.lrw_end)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {booking.speak_status === 'BOOKED' && booking.speak_date && (
                        <div className="bg-purple-50 p-3 rounded">
                          <h4 className="font-semibold text-sm mb-2">Speaking Session</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-2 text-gray-700">
                              <Calendar className="w-4 h-4 text-purple-600" />
                              <span>{formatDate(booking.speak_date)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                              <Clock className="w-4 h-4 text-purple-600" />
                              <span>{formatTime12Hour(booking.speak_time)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {booking.campus && (
                        <div className="flex items-center gap-2 text-gray-700 text-sm">
                          <Building2 className="w-4 h-4 text-purple-600" />
                          <span>{booking.campus}</span>
                        </div>
                      )}
                    </div>
                    {!isCancelable && booking.lrw_status === 'BOOKED' && (
                      <p className="mt-3 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                        ⚠️ Cannot cancel within 3 days of exam date
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Booking?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this booking? Your credit will be refunded.
            </DialogDescription>
          </DialogHeader>
          
          {cancelInfo && (
            <div className="py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-900">
                  Remaining Cancellation Chances This Month
                </p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {cancelInfo.remaining} / {cancelInfo.max_allowed}
                </p>
                {cancelInfo.remaining === 1 && (
                  <p className="text-xs text-amber-600 mt-2">
                    ⚠️ This is your last cancellation for this month
                  </p>
                )}
                {cancelInfo.remaining === 0 && (
                  <p className="text-xs text-red-600 mt-2">
                    ❌ You have used all cancellations for this month
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={canceling}
            >
              No, Keep Booking
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancel}
              disabled={canceling || (cancelInfo && cancelInfo.remaining === 0)}
            >
              {canceling ? 'Canceling...' : 'Yes, Cancel Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyBookingsTab;
